/**
 * Copyright (c) Meta Platforms, Inc. and affiliates.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 *
 */
import {
  DecoratorNode,
  DOMConversionMap,
  DOMConversionOutput,
  DOMExportOutput,
  LexicalNode,
  NodeKey,
  SerializedLexicalNode,
  Spread,
} from 'lexical';
import * as React from 'react';
import { Suspense } from 'react';

const FirmwareTableComponent = React.lazy(
  () => import('./FirmwareTableComponent.tsx'),
);

export type SerializedTableNode = Spread<
  {
    tableId: string;
  },
  SerializedLexicalNode
>;

function $convertFirmwareTableElement(
  domNode: HTMLElement,
): null | DOMConversionOutput {
  const tableId = domNode.getAttribute('data-lexical-table');
  if (tableId) {
    const node = $createFirmwareTableNode(tableId);
    return { node };
  }
  return null;
}

export class FirmwareTableNode extends DecoratorNode<React.JSX.Element> {
  __tableId: string;

  static getType(): string {
    return 'firmwareTable';
  }

  static clone(node: FirmwareTableNode): FirmwareTableNode {
    return new FirmwareTableNode(node.__tableId, node.__key);
  }

  static importJSON(serializedNode: SerializedTableNode): FirmwareTableNode {
    return $createFirmwareTableNode(serializedNode.tableId);
  }

  exportJSON(): SerializedTableNode {
    return {
      tableId: this.__tableId,
      type: 'firmwareTable',
      version: 1,
    };
  }

  constructor(tableId: string, key?: NodeKey) {
    super(key);
    this.__tableId = tableId;
  }

  exportDOM(): DOMExportOutput {
    const element = document.createElement('span');
    element.setAttribute('data-lexical-table', this.__tableId);
    return { element };
  }

  static importDOM(): DOMConversionMap | null {
    return {
      div: (domNode: HTMLElement) => {
        if (!domNode.hasAttribute('data-lexical-table')) {
          return null;
        }
        return {
          conversion: $convertFirmwareTableElement,
          priority: 1,
        };
      },
    };
  }

  createDOM(): HTMLElement {
    const elem = document.createElement('span');
    elem.style.display = 'inline-block';
    return elem;
  }

  updateDOM(): false {
    return false;
  }

  decorate(): React.JSX.Element {
    return (
      <Suspense fallback={null}>
        <FirmwareTableComponent
          nodeKey={this.getKey()}
          tableId={this.__tableId}
        />
      </Suspense>
    );
  }
}

export function $createFirmwareTableNode(tableId: string): FirmwareTableNode {
  return new FirmwareTableNode(tableId);
}

export function $isTableNode(
  node: FirmwareTableNode | LexicalNode | null | undefined,
): node is FirmwareTableNode {
  return node instanceof FirmwareTableNode;
}
